import { CopyIcon } from "@chakra-ui/icons";
import {
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Center,
  IconButton,
  Image,
  Text,
} from "@chakra-ui/react";
import { useStytch } from "@stytch/react";
import { useQuery } from "@tanstack/react-query";
import { AuthFactorSetupProps } from "features/auth/AuthFactor";
import { AuthHelpButton } from "features/auth/AuthHelpButton";
import { AuthOtpFormData } from "features/auth/AuthTypes";
import { AuthFactorRecoverySetup } from "features/auth/factor/recovery/AuthFactorRecoverySetup";
import {
  getStytchSessionDurationMinutes,
  useStytchFormErrorHandler,
} from "features/auth/StytchUtils";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { CardCloseButton } from "shared/components/card/CardCloseButton";
import { DeveloperZone } from "shared/components/developer/DeveloperZone";
import { LoadingState } from "shared/components/empty/LoadingState";
import Form from "shared/components/form/Form";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import TextInput from "shared/components/input/text/TextInput";
import { queryOptionsSingleShot } from "shared/utils/QueryUtils";
import { useBoolean } from "usehooks-ts";

export const AuthFactorTotpSetup: React.FC<AuthFactorSetupProps> = ({
  onCancel,
  onSuccess,
  rememberDevice,
}) => {
  const { t } = useTranslation(["common", "auth"]);

  const stytchClient = useStytch();
  const onStytchError = useStytchFormErrorHandler();

  const { data } = useQuery({
    queryKey: ["auth", "stytch", "totps"],
    queryFn: async () => {
      const unverifiedTotp = stytchClient.user
        .getSync()
        ?.totps.find((t) => !t.verified);
      if (unverifiedTotp) {
        await stytchClient.user.deleteTOTP(unverifiedTotp.totp_id);
      }
      return stytchClient.totps.create({
        expiration_minutes: 30,
      });
    },
    ...queryOptionsSingleShot,
  });

  const { value: isShowingRecoveryCodes, setTrue: showRecoveryCodes } =
    useBoolean();

  const form = useForm<AuthOtpFormData>();

  const onSubmit = async (data: AuthOtpFormData) =>
    await stytchClient.totps.authenticate({
      totp_code: data.code,
      session_duration_minutes: getStytchSessionDurationMinutes(rememberDevice),
    });

  if (isShowingRecoveryCodes) {
    return (
      <AuthFactorRecoverySetup
        onCancel={onCancel}
        onSuccess={onSuccess}
        recoveryCodes={data!.recovery_codes}
      />
    );
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      onSuccess={showRecoveryCodes}
      onError={onStytchError}
    >
      <Card width="md">
        <CardHeader pb={0}>
          <Box>
            <Text textStyle="h4">{t("auth:authenticate")}</Text>
            <Text pt="24px">{t("auth:setup.totpInstructions")}</Text>
          </Box>
          <CardCloseButton onClick={onCancel} />
        </CardHeader>
        <CardBody pb={0}>
          {data ? (
            <>
              <Center>
                <Image src={data.qr_code} />
                <input type="hidden" value={data.secret} />
              </Center>
              <DeveloperZone minWidth="400px">
                <TextInput
                  isReadOnly={true}
                  title={t("auth:developer.totpSecret")}
                  value={data.secret}
                  controlProps={{ mx: 4, my: 0 }}
                  rightElement={
                    <IconButton
                      aria-label=""
                      variant="ghost"
                      icon={<CopyIcon />}
                      onClick={() => {
                        void navigator.clipboard.writeText(data.secret);
                      }}
                    />
                  }
                />
              </DeveloperZone>
              <FormNumberInput
                name="code"
                title={t("auth:code")}
                autoFocus={true}
                maxLength={6}
                allowLeadingZeros={true}
                thousandSeparator={false}
                hideIcon={true}
              />
            </>
          ) : (
            <Box py="48px">
              <LoadingState />
            </Box>
          )}
        </CardBody>
        <CardFooter>
          <AuthHelpButton />
          <FormSubmitButton>{t("common:continue")}</FormSubmitButton>
        </CardFooter>
      </Card>
    </Form>
  );
};
